import { ref, computed, readonly } from 'vue'
import type { Id } from '~/convex/_generated/dataModel'

export interface PluginSessionConfig {
  clientId: string
  userId: string
  userName: string
  sessionToken: string
  productId: string
  convexUrl?: string
}

export interface PluginSessionData {
  pluginSessionToken: string
  chatRoomId: Id<'chatRooms'>
  internalUserId: Id<'users'>
  internalClientId: Id<'clients'>
  internalProductId: Id<'products'>
  externalUserId: string
  externalUserName: string
  externalClientId?: string
  externalProductId?: string
  convexUrl?: string
}

const STORAGE_KEY = 'abba_plugin_session'

const sessionData = ref<PluginSessionData | null>(null)
const isInitialized = ref(false)
const error = ref<string | null>(null)

export function usePluginSession() {
  /**
   * Initialize session with config
   */
  function setSession(data: PluginSessionData) {
    sessionData.value = data
    isInitialized.value = true
    error.value = null

    // Persist to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    } catch (e) {
      console.warn('Failed to persist session to localStorage:', e)
    }
  }

  /**
   * Load session from localStorage
   */
  function loadSession(): PluginSessionData | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const data = JSON.parse(stored) as PluginSessionData
        sessionData.value = data
        isInitialized.value = true
        return data
      }
    } catch (e) {
      console.warn('Failed to load session from localStorage:', e)
    }
    return null
  }

  /**
   * Clear session
   */
  function clearSession() {
    sessionData.value = null
    isInitialized.value = false
    error.value = null
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch (e) {
      console.warn('Failed to clear session from localStorage:', e)
    }
  }

  /**
   * Set error state
   */
  function setError(errorMessage: string) {
    error.value = errorMessage
  }

  return {
    // State
    sessionData: readonly(sessionData),
    isInitialized: readonly(isInitialized),
    error: readonly(error),

    // Computed
    chatRoomId: computed(() => sessionData.value?.chatRoomId),
    userId: computed(() => sessionData.value?.internalUserId),
    userName: computed(() => sessionData.value?.externalUserName),
    clientId: computed(() => sessionData.value?.internalClientId),
    productId: computed(() => sessionData.value?.internalProductId),

    // Methods
    setSession,
    loadSession,
    clearSession,
    setError
  }
}
